import React, { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import MaterialTable, { MTableToolbar } from 'material-table';
import { BOM_STAGES, DELIVERY_CHIP_COLORS, DELIVERY_ROW_BG, DELIVERY_STATUS, VALIDATION_STATUS} from 'assets/constants/Constants';
import { STAGE_COLORS } from 'assets/constants/Colors';
import { CALENDER_ICON, LIST_ICON, BOM_ICON, ArrowBackIos, ArrowForwardIos, Today, FormatListBulleted, LabelSharp, SpaceDashboardIcon} from 'assets/constants/Icons';
import moment from 'moment';
import Spinner from 'components/Spinner';
import { getBomList, dashboardData, getRegionById } from 'lib/axiosUtils';
import {
  ThemeProvider,
  createTheme,
  Chip,
  useMediaQuery,
  Container,
  Grid,
  Card,
  CardContent,
  Box,
  Typography,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Dialog,
  DialogContent,
} from '@mui/material';
import { IconButton, Button, ButtonGroup, Paper } from '@mui/material';
import Plot from 'react-plotly.js';
import { BarChart, StackedBarChart, Warning } from '@mui/icons-material';
import { DvrIcon } from 'assets/constants/Icons';
import AdminDashboardSkeleton from 'components/AdminDashboardSkeleton';
import { SALES, DESIGN_ENGINEER, PROCUREMENT_ENGINEER, LOGISTIC} from 'assets/constants/Constants';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import '../BOM/bom.css';
import BomProgressBar from '../BOM/BomProgressBar';
import { MANUFACTURING_ENGINEER } from 'assets/constants/Constants';
import { FONT_FAMILY } from 'assets/constants/Constants';

const localizer = momentLocalizer(moment);

const AdminDashboardPage = () => {
    const history = useHistory();
    const [bomList, setBomList] = useState([]);
    const [masterBomList, setMasterBomList] = useState([]);
    const [selectedStage, setSelectedStage] = useState('All');
    
    const [selectedVertical, setSelectedVertical] = useState('All');
    const [selectedRegion, setSelectedRegion] = useState('All');
    const [verticalOptions, setVerticalOptions] = useState([]);
    const [regionOptions, setRegionOptions] = useState([]);

    const [delayedBoms, setDelayedBoms] = useState([]);
    const [deliveryNextTwoWeeks, setDeliveryNextTwoWeeks] = useState([]);
    const [partialProcurementList, setPartialProcurementList] = useState([]); 
    const [isLoading, setIsLoading] = useState(true);
    const [metrics, setMetrics] = useState({});
    const [currentView, setCurrentView] = useState('month');
    const isXs = useMediaQuery('(max-width:600px)');
    const isSm = useMediaQuery('(max-width:900px)');
    const [eventDialogOpen, setEventDialogOpen] = useState(false);
    const [selectedEventData, setSelectedEventData] = useState(null);
    const [agendaMonthFilter, setAgendaMonthFilter] = useState(moment().format('MMM YYYY'));
    const [currentDate, setCurrentDate] = useState(new Date());

    const handleNavigate = (newDate) => {
        setCurrentDate(newDate);
        setAgendaMonthFilter(moment(newDate).format('MMM YYYY'));
    };

    const handleSelectEvent = (event) => {
        setSelectedEventData(event.resource);
        setEventDialogOpen(true);
    };
    
//Logged In user details:
    const user = JSON.parse(localStorage.getItem('userData'));
    const isSales = user?.user_role?.toLowerCase() === SALES;
    const userRegions = Array.isArray(user?.region_id) ? user.region_id : [];
    const isDesignEngineer = user?.user_role?.toLowerCase() === DESIGN_ENGINEER;
    const isProcurementEngineer = user?.user_role?.toLowerCase() === PROCUREMENT_ENGINEER;
    const isLogistic = user?.user_role?.toLowerCase() === LOGISTIC;
    const isME = user?.user_role?.toLowerCase() === MANUFACTURING_ENGINEER;

    const handleNavigateToPlan = (bomId) => {
        history.push(`/plan-bom/${bomId}`);
    };

    const fieldsToShow = [
        { key: 'designEngineerName', label: 'Design Engineer' },
        { key: 'documentNumber', label: 'Fixture Document Number' },
        { key: 'fixtureName', label: 'Fixture Name' },
        { key: 'manufacturingReleaseDate', label: 'Manufacturing Release Date' },
        { key: 'customerName', label: 'Customer' },
        { key: 'machine', label: 'Machine' },
        { key: 'componentdrawingNo', label: 'Component Drawing Number(s)' },
        { key: 'poReference', label: 'PO Reference' },
        { key: 'dapDate', label: 'DAP Date' },
        { key: 'surfaceTreatmentType', label: 'Surface Treatment Type' },
        { key: 'businessVerticalName', label: 'Business Vertical' },
        { key: 'regionName', label: 'Region' }
    ];

    const dispatch_details = [
        { key: 'dispatch_via', label: 'Dispatch Via' },
        { key: 'awb_number', label: 'AWB Number' },
        { key: 'mode_of_dispatch', label: 'Mode of Dispatch' },
        { key: 'dispatch_date', label: 'Dispatch Date' }
    ]

    const filterStages = [
      'All',
      BOM_STAGES.MANUFACTURING_EXECUTION,
      BOM_STAGES.TESTING,
      BOM_STAGES.DISPATCH,
      BOM_STAGES.COMMERCIAL_CLEARANCE,
      BOM_STAGES.HANDOVER_TO_DISPATCH,
      BOM_STAGES.COMPLETED,
      BOM_STAGES.VALIDATION,
    ];

    const mytheme = createTheme({});
    const getRowBackground = (rowData) => {
        if (rowData.stage === BOM_STAGES.HANDOVER_TO_DISPATCH || rowData.stage === BOM_STAGES.COMPLETED || rowData.stage === BOM_STAGES.VALIDATION) {
          return 'transparent';
        }
        const key = rowData.current_tracking_status;
        return DELIVERY_ROW_BG[key] || 'transparent';
    };

    const getStatusLabel = (status) => {
        if (!status) return 'N/A';
        return status;
    };

    useEffect(() => {
      (async () => {
        await fetchBomData();
      })();
    }, []);

    useEffect(() => {
      fetchMetrics(selectedVertical, selectedRegion);
    }, [selectedVertical, selectedRegion]);
    useEffect(() => {
      if (masterBomList.length > 0) {
        applyFiltersToData(selectedVertical, selectedRegion, selectedStage);
      }
    }, [selectedVertical, selectedRegion, selectedStage, masterBomList]);

    // --- Helper to get Region Names for Sales Users ---
    const getUserRegionNames = async () => {
      let regionNames = [];
      if (isSales && userRegions.length > 0) {
        try {
          const regionPromises = userRegions.map((id) => getRegionById({}, {}, id));
          const regionResponses = await Promise.all(regionPromises);
          regionNames = regionResponses
            .map((res) => res?.data?.result?.name || res?.data?.result?.region_name)
            .filter(Boolean);
        } catch (error) {
          console.error('Error fetching region names:', error);
        }
      }
      return { isSales, regionNames };
    };

    const calendarEvents = useMemo(() => {
    let filteredData = masterBomList;

    if (selectedVertical !== 'All') {
      filteredData = filteredData.filter(item => item.businessVerticalId === selectedVertical);
    }
    if (selectedRegion !== 'All') {
      filteredData = filteredData.filter(item => item.regionId === selectedRegion);
    }

    return filteredData
      .filter(bom => bom.manufacturingDelivery && bom.stage !== BOM_STAGES.VALIDATION && bom.stage !== BOM_STAGES.COMPLETED)
      .map(bom => {
        const dateObj = moment(bom.manufacturingDelivery, 'DD-MM-YYYY');
        
        if (!dateObj.isValid()) return null;

        return {
          title: `${bom.customerName} - ${bom.documentNumber}`,
          start: dateObj.toDate(),
          end: dateObj.toDate(),
          allDay: true,
          resource: bom
        };
      })
      .filter(Boolean);
  }, [masterBomList, selectedVertical, selectedRegion]);

  const CustomEvent = ({ event }) => {
    const { customerName, documentNumber, machine, stage, current_tracking_status } = event.resource;
    
    const tooltipContent = (
      <Box sx={{ p: 0.5 }}>
        <div style={{ fontWeight: 'bold', borderBottom: '1px solid #eee', marginBottom: '5px', fontSize: '12px' }}>
          {customerName}
        </div>
        <div style={{ fontSize: '10px', display: 'block' }}>Fixture: {documentNumber}</div>
        <div style={{ fontSize: '10px', display: 'block' }}>Machine: {machine || 'N/A'}</div>
        <div style={{ fontSize: '10px', display: 'block' }}>Stage: {stage}</div>
        <div style={{ fontSize: '10px', display: 'block' }}>
            Status: {current_tracking_status || 'N/A'}
        </div>
      </Box>
    );

    return (
      <Tooltip title={tooltipContent} arrow placement="top" 
      PopperProps={{
            sx: { zIndex: 9999999} 
        }}>
        <div style={{ padding: '1px 1px', lineHeight: '1.1' }}>
          <div style={{ fontSize: '10px' }}>{customerName}</div>
          <div style={{ fontSize: '10px', opacity: 0.9 }}>{documentNumber}</div>
        </div>
      </Tooltip>
    );
  };

const eventStyleGetter = (event) => {
    if (currentView === 'agenda') {
        return {};
    }
    const status = event.resource.current_tracking_status;
    const colorStyle = DELIVERY_ROW_BG[status] ? DELIVERY_CHIP_COLORS[status] : { background: '#ffffffff', text: 'white' };
    
    return {
      style: {
        backgroundColor: colorStyle?.background || '#ffffffff',
        color: colorStyle?.text || 'white',
        borderRadius: '4px',
        border: 'none',
        // display: 'block',
        opacity: 0.9,
      }
    };
  };
  const CustomCalendarToolbar = (toolbar) => {
  const goToBack = () => {
    toolbar.onNavigate('PREV');
  };

  const goToNext = () => {
    toolbar.onNavigate('NEXT');
  };

  const goToCurrent = () => {
    toolbar.onNavigate('TODAY');
  };

  const handleViewChange = (view) => {
    toolbar.onView(view);
  };
  const date = moment(toolbar.date);
  // Monthly BOM count
    const monthBomCount = (toolbar.calendarEvents || []).filter(event =>
      moment(event.start).isSame(date, 'month')
    ).length;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, mt: -5, p: 1, gap: 1}}>
         <IconButton onClick={goToBack} size="small" sx={{ border: '0px solid #e0e0e0' }}>
            <ArrowBackIos fontSize="inherit" />
         </IconButton>
                 <Box
          sx={{
            '& *': {
              fontSize: '16px !important',
            },
          }}
        >
         <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#2d3d42' }}>
          {date.format('MMMM YYYY')}
        </span>
        </Box>
         <IconButton onClick={goToNext} size="small" sx={{ border: '0px solid #e0e0e0' }}>
            <ArrowForwardIos fontSize="inherit" />
         </IconButton>
         <span
            style={{
              marginLeft: 8,
              fontSize: '16px',
              fontWeight: 700,
              color: '#2d3d42',
            }}
          >
            {monthBomCount}&nbsp;&nbsp;&nbsp;BOM Deliveries
          </span>
    </Box>
  );
};

    const fetchMetrics = async (verticalId, regionId) => {
      try {
        const STATUS_ORDER = ['On Time', 'Warning', 'Delayed'];
        const STAGE_ORDER = [
            BOM_STAGES.DESIGN,
            BOM_STAGES.PROCUREMENT,
            BOM_STAGES.MANUFACTURING_PLANNING,
            BOM_STAGES.MANUFACTURING_EXECUTION,
            BOM_STAGES.TESTING,
            BOM_STAGES.DISPATCH,
            BOM_STAGES.COMMERCIAL_CLEARANCE,
            BOM_STAGES.HANDOVER_TO_DISPATCH,
            BOM_STAGES.COMPLETED
        ];

        // if (isSales) {
        //    if (masterBomList.length === 0 && isLoading) return;

        //    let salesFiltered = masterBomList;

        //    if (verticalId && verticalId !== 'All') {
        //      salesFiltered = salesFiltered.filter(b => b.businessVerticalId === verticalId);
        //    }
        //    if (regionId && regionId !== 'All') {
        //      salesFiltered = salesFiltered.filter(b => b.regionId === regionId);
        //    }

        //    const totalBOMs = salesFiltered.length;
        //    const stageCountsMap = {};
        //    const statusCountsMap = {};

        //    salesFiltered.forEach((bom) => {
        //      if (bom.stage) {
        //        stageCountsMap[bom.stage] = (stageCountsMap[bom.stage] || 0) + 1;
        //      }
        //      if (bom.current_tracking_status) {
        //        statusCountsMap[bom.current_tracking_status] = (statusCountsMap[bom.current_tracking_status] || 0) + 1;
        //      }
        //    });

        //    const finalStageCounts = STAGE_ORDER.map(stage => ({
        //        stage: stage,
        //        count: stageCountsMap[stage] || 0
        //    }));

        //    const finalStatusCounts = STATUS_ORDER.map(status => ({
        //        status: status,
        //        count: statusCountsMap[status] || 0
        //    }));

        //    const verticalCountsMap = {};
        //    const regionCountsMap = {};

        //    // Initialize counts based on ALL master list items to capture names
        //    masterBomList.forEach(bom => {
        //         if (bom.businessVerticalId && bom.businessVerticalName) {
        //             if (!verticalCountsMap[bom.businessVerticalId]) {
        //                 verticalCountsMap[bom.businessVerticalId] = { id: bom.businessVerticalId, name: bom.businessVerticalName, count: 0 };
        //             }
        //         }
        //         if (bom.regionId && bom.regionName) {
        //             if (!regionCountsMap[bom.regionId]) {
        //                 regionCountsMap[bom.regionId] = { id: bom.regionId, name: bom.regionName, count: 0 };
        //             }
        //         }
        //    });

        //    // Count Ongoing based on filters
        //    let verticalDataSrc = masterBomList;
        //    if (regionId && regionId !== 'All') verticalDataSrc = masterBomList.filter(b => b.regionId === regionId);
           
        //    verticalDataSrc.forEach(bom => {
        //        // Only count if stage is "Ongoing" (exclude Completed/Dispatch if needed, or check logic)
        //        if (bom.businessVerticalId && verticalCountsMap[bom.businessVerticalId]) {
        //            // Add logic here if you only want to count "Ongoing" BOMs for this specific list
        //            // For now, counting all based on your previous logic
        //            verticalCountsMap[bom.businessVerticalId].count += 1;
        //        }
        //    });

        //    let regionDataSrc = masterBomList;
        //    if (verticalId && verticalId !== 'All') regionDataSrc = masterBomList.filter(b => b.businessVerticalId === verticalId);
           
        //    regionDataSrc.forEach(bom => {
        //        if (bom.regionId && regionCountsMap[bom.regionId]) {
        //            regionCountsMap[bom.regionId].count += 1;
        //        }
        //    });

        //    // 5. Calculate Dispatched FY (Fill 0s for all verticals)
        //    const now = moment();
        //    const currentYear = now.year();
        //    const currentMonth = now.month(); // 0-11
        //    let fyStartYear = currentMonth >= 3 ? currentYear : currentYear - 1;
        //    const fyStart = moment(`${fyStartYear}-04-01`);
        //    const fyEnd = moment(`${fyStartYear + 1}-03-31`);

        //    const dispatchedMap = {};
        //    // Initialize map with 0 for all known verticals
        //    Object.values(verticalCountsMap).forEach(v => {
        //        dispatchedMap[v.name] = 0;
        //    });

        //    salesFiltered.forEach(bom => {
        //        const isDispatched = bom.stage === BOM_STAGES.DISPATCH || bom.stage === BOM_STAGES.COMPLETED;
        //        const delivery = bom.manufacturingDelivery ? moment(bom.manufacturingDelivery, 'DD-MM-YYYY') : null;
               
        //        if (isDispatched && delivery && delivery.isBetween(fyStart, fyEnd, 'day', '[]')) {
        //             const vName = bom.businessVerticalName || 'Unknown';
        //             // Only increment if we have a name, else ignore or add to 'Unknown'
        //             dispatchedMap[vName] = (dispatchedMap[vName] || 0) + 1;
        //        }
        //    });
           
        //    const dispatchedFY = Object.entries(dispatchedMap).map(([name, count]) => ({ name, count }));

        //    setMetrics({
        //      totalBOMs,
        //      stageCounts: finalStageCounts,
        //      statusCounts: finalStatusCounts,
        //      ongoingByVertical: Object.values(verticalCountsMap),
        //      ongoingByRegion: Object.values(regionCountsMap),
        //      dispatchedFY: dispatchedFY, 
        //    });

        // } else {
          const params = {};
          if (verticalId && verticalId !== 'All') params.business_vertical_id = verticalId;
          if (regionId && regionId !== 'All') params.region_id = regionId;
          const response = await dashboardData({ params }); 
          
          if (response?.data?.result) {
            setMetrics(response.data.result);
          }
        // }
      } catch (error) {
        console.error('Error fetching metrics:', error);
      }
    };
    const formatDateString = (dateString) => {
      if (!dateString) return '';
      const [day, month, year] = dateString.split('-');
      const date = new Date(`${year}-${month}-${day}`);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
    };

    const addBusinessDays = (startDate, days) => {
        let currentDate = new Date(startDate);
        let added = 0;
        if (days <= 0) return currentDate;
        while (added < days) {
            currentDate.setDate(currentDate.getDate() + 1);
            if (currentDate.getDay() !== 0) {
                added++;
            }
        }
        return currentDate;
    };

    const fetchBomData = async () => {
        try {
            const { isSales, regionNames } = await getUserRegionNames();
            
            const response = await getBomList();
            if (response && response.data && response.data.result) {
                let data = response.data.result;
                
                // if (isSales && regionNames.length > 0) {
                //     data = data.filter(bom => 
                //         regionNames.includes(bom.regionName) || 
                //         regionNames.includes(bom.region_id?.name)
                //     );
                // }
                // --------------------------

                const allMappedData = data.map((bom, index) => ({
                            index: index + 1,
                            bomId: bom._id,
                            documentNumber: bom.fixture_document_number,
                            fixtureName: bom.fixture_name,
                            customerName: bom.customerName || 'N/A',
                            end_client: bom.end_client || '',
                            businessVerticalName: bom.businessVerticalName || 'N/A',
                            regionName: bom.regionName || 'N/A',
                            
                            businessVerticalId: bom.business_vertical_id || null,
                            regionId: bom.region_id || null,

                            machine: bom.machine || '',
                            componentdrawingNo: bom.component_drawing_numbers || [],
                            dapDate: bom.dap_date ? moment(bom.dap_date).format('DD-MM-YYYY') : '',
                            manufacturingDelivery: bom.projected_completion_date
                                ? moment(addBusinessDays(bom.projected_completion_date, bom.testing_buffer_days || 0)).format('DD-MM-YYYY')
                                : '',
                            stage: bom.current_stage,
                            poReference: bom.po_reference || '',
                            surfaceTreatmentType: bom.surface_treatment_type || '',
                            procurementDelivery: bom.procurement_expected_delivery_date
                                ? moment(bom.procurement_expected_delivery_date).format('DD-MM-YYYY')
                                : '',
                            createdDateTime: bom.created_date_time
                                ? moment(bom.created_date_time).format('DD-MM-YYYY HH:mm:ss')
                                : '',
                            updatedDateTime: bom.updated_date_time
                                ? moment(bom.updated_date_time).format('DD-MM-YYYY HH:mm:ss')
                                : '',
                            numberOfItems: bom.items ? bom.items.length : 0,
                            approvedBusinessVertical: bom.approved_business_vertical || false,
                            currentStage: bom.current_stage || '',
                            designEngineerId: bom.design_engineer_id || '',
                            fixtureReadyToDispatch: bom.fixture_ready_to_dispatch || false,
                            completion_percentage: bom.completion_percentage || 0,
                            current_tracking_status: bom.current_tracking_status || '',
                            items: bom.items || [],
                            manufacturingReleaseDate: bom.manufacturing_release_date
                                ? moment(bom.manufacturing_release_date).format('DD-MM-YYYY')
                                : '',
                            mfgDrawingsAndBomSent: bom.mfg_drawings_and_bom_sent || false,
                            mfgExpectedDeliveryPossible: bom.mfg_expected_delivery_possible || false,
                            testingDone: bom.testing_done || false,
                            partialProcurement: bom.partial_procurement || false,
                            regionName: bom.regionName || 'N/A',
                            designEngineerName: bom.designEngineerName || '',
                            stage_timelines: bom.stage_timelines || {},
                            dispatch_details: bom.dispatch_details || {},
                    }));

                const uniqueVerticals = [];
                const verticalMap = new Map();
                allMappedData.forEach(item => {
                  if (item.businessVerticalId && item.businessVerticalName && !verticalMap.has(item.businessVerticalId)) {
                    verticalMap.set(item.businessVerticalId, true);
                    uniqueVerticals.push({ id: item.businessVerticalId, name: item.businessVerticalName });
                  }
                });
                setVerticalOptions(uniqueVerticals);

                const uniqueRegions = [];
                const regionMap = new Map();
                allMappedData.forEach(item => {
                  if (item.regionId && item.regionName && !regionMap.has(item.regionId)) {
                    regionMap.set(item.regionId, true);
                    uniqueRegions.push({ id: item.regionId, name: item.regionName });
                  }
                });
                setRegionOptions(uniqueRegions);

                setMasterBomList(allMappedData);
                
                setIsLoading(false);
            }
        } catch (error) {
            console.error('Error fetching BOMs:', error);
            setIsLoading(false);
        }
    };

    const applyFiltersToData = (vId, rId, sStage) => {
      let filtered = masterBomList;

      // 1. Apply Global Filters (Vertical & Region)
      if (vId !== 'All') {
        filtered = filtered.filter(item => item.businessVerticalId === vId);
      }
      if (rId !== 'All') {
        filtered = filtered.filter(item => item.regionId === rId);
      }

      // 2. Calculate "Next Two Weeks" based on Global Filtered Data
      const today = moment();
      const twoWeeksLater = moment().add(14, 'days');
      const nextTwoWeeks = filtered.filter(bom => {
          if (bom.stage === BOM_STAGES.COMPLETED || bom.stage === BOM_STAGES.VALIDATION) return false; 
          if (!bom.manufacturingDelivery) return false;
          const delivery = moment(bom.manufacturingDelivery, 'DD-MM-YYYY');
          return delivery.isValid() && delivery.isBetween(today, twoWeeksLater, 'day', '[]');
      });

      nextTwoWeeks.sort((a, b) => {
      const dateA = moment(a.manufacturingDelivery, 'DD-MM-YYYY');
      const dateB = moment(b.manufacturingDelivery, 'DD-MM-YYYY');
      return dateA - dateB; 
      });
      setDeliveryNextTwoWeeks(nextTwoWeeks);

      const partialList = filtered.filter(bom => bom.partialProcurement === true);
    
    // Sort by procurement deadline (ascending) to show urgent first
    partialList.sort((a, b) => {
        const dateA = moment(a.procurementDelivery || a.manufacturingDelivery, 'DD-MM-YYYY');
        const dateB = moment(b.procurementDelivery || b.manufacturingDelivery, 'DD-MM-YYYY');
        return dateA - dateB;
    });
    
    setPartialProcurementList(partialList);
    // ----------------------

      // 3. Apply Stage Filter (for Main Table)

      const allowedStagesForTable = [
        BOM_STAGES.DESIGN,
        BOM_STAGES.PROCUREMENT,
        BOM_STAGES.MANUFACTURING_PLANNING,
        BOM_STAGES.MANUFACTURING_EXECUTION,
        BOM_STAGES.TESTING,
        BOM_STAGES.DISPATCH,
        BOM_STAGES.COMMERCIAL_CLEARANCE,
        BOM_STAGES.COMPLETED,
        BOM_STAGES.HANDOVER_TO_DISPATCH,
        BOM_STAGES.VALIDATION,
      ];

      let tableData = filtered.filter(bom => allowedStagesForTable.includes(bom.currentStage));

      if (sStage !== 'All') {
        tableData = tableData.filter(bom => bom.stage === sStage);
      }

      setBomList(tableData);

      // 4. Update Delayed BOMs (Alert Table)

      let attentionList = [];

      if (isDesignEngineer) {
        // DESIGN ENGINEER → all DESIGN stage BOMs
        attentionList = tableData.filter(
          (bom) => bom.current_tracking_status === 'Delayed' &&  
          bom.stage === BOM_STAGES.DESIGN
        );

      } else if (isProcurementEngineer) {
        // PROCUREMENT ENGINEER → Partial Procurement BOMs only
        attentionList = tableData.filter(
          (bom) => bom.current_tracking_status === 'Delayed' &&  
          bom.partialProcurement === true
        );

      } else if (isSales) {
        // SALES → Region-wise Delayed BOMs ONLY
        attentionList = masterBomList.filter(
          (bom) =>
          bom.current_tracking_status === 'Delayed' &&
          userRegions.includes(bom.regionId) || bom?.validation_details?.validation_status !== VALIDATION_STATUS.DONE
        );
      
      } else if( isLogistic){
        attentionList = tableData.filter(
          (bom) => bom.current_tracking_status === 'Delayed' && 
          bom.stage === BOM_STAGES.HANDOVER_TO_DISPATCH
        );
      } else if(isME ){
        attentionList = tableData.filter(
          (bom) => bom.current_tracking_status === 'Delayed' && 
          bom.stage !== BOM_STAGES.HANDOVER_TO_DISPATCH
        );

      } else {
        // Delayed BOMs
        attentionList = tableData.filter(
          (bom) => bom.current_tracking_status === 'Delayed'
        );
      }
      attentionList = attentionList.map(bom => ({
        ...bom,
        bomId: bom.bomId,
      }));
      setDelayedBoms(attentionList);
    };


  const handleStageChange = (event, newStage) => {

    if (newStage !== null) {
      setSelectedStage(newStage);
    }
  };

  const statusColors = useMemo(
    () =>
      (metrics.statusCounts || []).map(
        (s) => (DELIVERY_CHIP_COLORS[s.status] || { background: '#9e9e9e' }).background
      ),
    [metrics.statusCounts]
  );

  const getStageColor = (stageLabel) => {
    switch (stageLabel) {
      case BOM_STAGES.DESIGN:
        return STAGE_COLORS.DESIGN;
      case BOM_STAGES.PROCUREMENT:
        return STAGE_COLORS.PROCUREMENT;
      case BOM_STAGES.MANUFACTURING_PLANNING:
        return STAGE_COLORS.MANUFACTURING_PLANNING;
      case BOM_STAGES.MANUFACTURING_EXECUTION:
        return STAGE_COLORS.MANUFACTURING_EXECUTION;
      case BOM_STAGES.TESTING:
        return STAGE_COLORS.TESTING;
      case BOM_STAGES.DISPATCH:
        return STAGE_COLORS.DISPATCH;
      case BOM_STAGES.HANDOVER_TO_DISPATCH:
        return STAGE_COLORS.HANDOVER_TO_DISPATCH;
      case BOM_STAGES.COMPLETED:
        return STAGE_COLORS.COMPLETED;
      case BOM_STAGES.COMMERCIAL_CLEARANCE:
        return STAGE_COLORS.COMMERCIAL_CLEARANCE;
      case 'All':
        return '#9e9e9e';
        case BOM_STAGES.VALIDATION:
        return STAGE_COLORS.VALIDATION;
      default:
        return STAGE_COLORS.DEFAULT || '#9e9e9e';
    }
  };

  const stagePlotColors = useMemo(
    () => (metrics.stageCounts || []).map((s) => getStageColor(s.stage)),
    [metrics.stageCounts]
  );

  // ------------------------------------------------
  // LOGIC TO SYNC CHART Y-AXIS
  // ------------------------------------------------
  const commonYAxisMax = useMemo(() => {
    const statusCounts = metrics.statusCounts || [];
    const stageCounts = metrics.stageCounts || [];


    const maxStatus = statusCounts.length > 0 
        ? Math.max(...statusCounts.map(s => s.count)) 
        : 0;

    const maxStage = stageCounts.length > 0 
        ? Math.max(...stageCounts.map(s => s.count)) 
        : 0;

    return Math.max(maxStatus, maxStage) + 5;
  }, [metrics.statusCounts, metrics.stageCounts]);
  // ------------------------------------------------

const handleCustomExport = (columns, data) => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Project List');

    const exportableColumns = columns
        .filter(col => !col.hidden && col.field)
        .map(col => ({
            header: col.title,
            key: col.field,
            width: 10
        }));

    worksheet.columns = exportableColumns;
    const headerRow = worksheet.getRow(1);

    const maxWidths = {};
    exportableColumns.forEach(col => {
        maxWidths[col.key] = String(col.header).length; 
    });

    data.forEach(rowData => {
        exportableColumns.forEach(col => {
            let cellValue = rowData[col.key];
            if (Array.isArray(cellValue)) {
                cellValue = cellValue.join(', ');
            } else {
                cellValue = cellValue ? String(cellValue) : '-';
            }
            
            if (col.key !== 'componentdrawingNo') {
                maxWidths[col.key] = Math.max(maxWidths[col.key], cellValue.length);
            }
        });
    });

    exportableColumns.forEach(col => {
        if (col.key === 'componentdrawingNo') {
            col.width = 40;
        } else {
            col.width = maxWidths[col.key] + 2;
        }
    });
    worksheet.columns = exportableColumns;

    headerRow.eachCell((cell) => {
        cell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFDCE6F1' }
        };
        cell.font = { bold: true, color: { argb: 'FF000000' }, size: 12 };
        cell.alignment = { vertical: 'middle', horizontal: 'left' };
        cell.border = { bottom: { style: 'thin' } };
    });

    const STATUS_COLORS_ARGB = {
        'On Time': 'FFE8F5E9',
        'Warning': 'FFFFF3E0',
        'Delayed': 'FFFFDAD4',
    };

    data.forEach(rowData => {
        const row = {};
        exportableColumns.forEach(col => {
            if (Array.isArray(rowData[col.key])) {
                row[col.key] = rowData[col.key].join(', ');
            } else {
                row[col.key] = rowData[col.key] || '-';
            }
        });
        const addedRow = worksheet.addRow(row);

        const status = rowData.current_tracking_status;
        if (status && STATUS_COLORS_ARGB[status]) {
            addedRow.eachCell({ includeEmpty: true }, (cell) => {
                cell.fill = {
                    type: 'pattern',
                    pattern: 'solid',
                    fgColor: { argb: STATUS_COLORS_ARGB[status] }
                };
            });
        }
    });


    worksheet.getColumn('componentdrawingNo').alignment = { 
        vertical: 'top', 
        horizontal: 'left', 
        wrapText: true 
    };

    workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, `Project_List_${moment().format('YYYY-MM-DD')}.xlsx`);
    });
  };

const handleExportPdf = (columns, data) => {
    const doc = new jsPDF('l', 'mm', 'a4');
    const logoUrl = '/images/logo512.png';

    const exportableColumns = columns
        .filter(col => !col.hidden && col.field)
        .map(col => ({
            header: col.title,
            dataKey: col.field
        }));

    const tableData = data.map(rowData => {
        const row = {};
        exportableColumns.forEach(col => {
            if (col.dataKey === 'componentdrawingNo' && Array.isArray(rowData[col.dataKey])) {
                const items = rowData[col.dataKey];
                const itemsPerLine = 4;
                let formattedText = [];

                for (let i = 0; i < items.length; i += itemsPerLine) {
                    formattedText.push(items.slice(i, i + itemsPerLine).join(', '));
                }
                
                row[col.dataKey] = formattedText.join('\n'); 
            } 
            else {
                row[col.dataKey] = rowData[col.dataKey] ? String(rowData[col.dataKey]) : '-';
            }
        });
        row._status = rowData.current_tracking_status;
        return row;
    });

    const STATUS_COLORS_RGB = {
        'On Time': [232, 245, 233],   
        'Warning': [255, 243, 224],   
        'Delayed': [255, 218, 212],   
    };

    doc.autoTable({
        startY: 30,
        margin: { top: 30 },
        columns: exportableColumns,
        body: tableData,
        styles: { fontSize: 8, cellPadding: 1 },
        headStyles: {
            fillColor: [220, 230, 241],
            textColor: [0, 0, 0],
            fontStyle: 'bold',
            lineWidth: 0, 
            lineColor: [0, 0, 0] 
        },
        theme: 'grid',
        didParseCell: (data) => {
            if (data.section === 'body' && data.row.raw._status) {
                const status = data.row.raw._status;
                if (STATUS_COLORS_RGB[status]) {
                    data.cell.styles.fillColor = STATUS_COLORS_RGB[status];
                }
            }
        },
        didDrawPage: (data) => {
             try {
                const imgProps = doc.getImageProperties(logoUrl);
                const pdfLogoHeight = 15; 
                const pdfLogoWidth = (imgProps.width * pdfLogoHeight) / imgProps.height;
                doc.addImage(logoUrl, 'JPEG', data.settings.margin.left, 10, pdfLogoWidth, pdfLogoHeight);
            } catch (err) {
                console.warn("Logo could not be loaded", err);
            }
            const str = 'Downloaded on: ' + moment().format('DD-MMM-YYYY HH:mm:ss');
            doc.setFontSize(8);
            const pageSize = doc.internal.pageSize;
            const pageHeight = pageSize.height ? pageSize.height : pageSize.getHeight();
            doc.text(str, data.settings.margin.left, pageHeight - 10);
        }
    });

    doc.save(`Project_List_${moment().format('YYYY-MM-DD')}.pdf`);
  };

  const mainTableOptions = useMemo(
    () => ({
        headerStyle: {
            fontWeight: 'bolder',
            fontSize: 14,
            textAlign: 'center'
        },
        exportAllData: true,
        filtering: true,
        sorting: true,
        columnsButton: true,
        exportButton: true,
        exportFileName: 'Project List',
        pageSize: isXs ? 5 : 10,
        pageSizeOptions: isSm ? [5, 10] : [5, 10, 20],
        padding: 'dense',
        toolbar: true,
        search: true,
        tableLayout: 'auto',
        cellStyle: {
          fontSize : 12,
          padding:-1
        },
        exportCsv: (columns, data) => {
            handleCustomExport(columns, data);
        },
        exportPdf: (columns, data) => {
            handleExportPdf(columns, data);
        }
    }),
    [isXs, isSm]
  );

                  const mainTableColumns = useMemo(
                    () => [
                                        { title: 'Business Vertical', field: 'businessVerticalName', width:'40%' },
                                        { title: 'Region', field: 'regionName' },
                                        { title: 'Fixture No.', field: 'documentNumber' },
                                        { title: 'Customer', field: 'customerName' },
                                        { title: 'End Client', field: 'end_client' },
                                        { title: 'Machine', field: 'machine' },
                                        {
                                          title: 'Comp. Drawing No.',
                                          field: 'componentdrawingNo',
                                          render: (rowData) => {
                                            const text = Array.isArray(rowData.componentdrawingNo)
                                              ? rowData.componentdrawingNo.join(', ')
                                              : rowData.componentdrawingNo;

                                            return (
                                              <div
                                                style={{
                                                  whiteSpace: 'nowrap',
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                                }}
                                                title={text} // optional: show full text on hover
                                              >
                                                {text}
                                              </div>
                                            );
                                          },
                                          cellStyle: { maxWidth: 150 }, // optional: limit cell width
                                        },
                                        { title: 'DAP Date', 
                                          field: 'dapDate', 
                                          width:'5%',
                                          customSort: (a, b) => {
                                            const parseDate = (d) => {
                                              if (!d) return 0;
                                              const [day, month, year] = d.split('-');
                                              return new Date(year, month - 1, day);
                                            };
                                            return parseDate(a.dapDate) - parseDate(b.dapDate);
                                          }
                                        },
                                        { title: 'Delivery Date', 
                                          field: 'manufacturingDelivery', 
                                          width:'5%',
                                          customSort: (a, b) => {
                                            const parseDate = (d) => {
                                              if (!d) return 0;
                                              const [day, month, year] = d.split('-');
                                              return new Date(year, month - 1, day);
                                            };
                                            return parseDate(a.manufacturingDelivery) - parseDate(b.manufacturingDelivery);
                                          }
                                        },
                                        {
                                        title: 'Current Milestone',
                                        field: 'stage',
                                        render: (rowData) => {
                                            const chipStyle = { width: 140, fontWeight: 500,fontSize: '10px'};
                                            let backgroundColor;
                                            let textColor = 'black';
                                            const stage = rowData.stage;
                                            switch (stage) {
                                                case BOM_STAGES.DESIGN: backgroundColor = STAGE_COLORS.DESIGN; break;
                                                case BOM_STAGES.PROCUREMENT: backgroundColor = STAGE_COLORS.PROCUREMENT; break;
                                                case BOM_STAGES.MANUFACTURING_PLANNING: backgroundColor = STAGE_COLORS.MANUFACTURING_PLANNING; break;
                                                case BOM_STAGES.MANUFACTURING_EXECUTION: backgroundColor = STAGE_COLORS.MANUFACTURING_EXECUTION; break;
                                                case BOM_STAGES.TESTING: backgroundColor = STAGE_COLORS.TESTING; break;
                                                case BOM_STAGES.DISPATCH: backgroundColor = STAGE_COLORS.DISPATCH; break;
                                                case BOM_STAGES.HANDOVER_TO_DISPATCH: backgroundColor = STAGE_COLORS.HANDOVER_TO_DISPATCH; break;
                                                case BOM_STAGES.COMPLETED: backgroundColor = STAGE_COLORS.COMPLETED; break;
                                                case BOM_STAGES.COMMERCIAL_CLEARANCE: backgroundColor = STAGE_COLORS.COMMERCIAL_CLEARANCE; break;
                                                case BOM_STAGES.VALIDATION: backgroundColor = STAGE_COLORS.VALIDATION; break;
                                                default: backgroundColor = '#616161'; break;
                                            }
                                            const displayStage = rowData.stage
                                            ? rowData.stage.charAt(0).toUpperCase() + rowData.stage.slice(1)
                                            : 'N/A';
                                            return <Chip label={displayStage} size="small" style={{ ...chipStyle, fontSize: 10, backgroundColor, color: textColor }} />;
                                        },
                                        },
                                        { 
                                            title: 'Status', 
                                            field: 'current_tracking_status',
                                            width: '5%',
                                            render: (rowData) => {
                                              const isCompletedDone =
                                              ((rowData.stage === BOM_STAGES.HANDOVER_TO_DISPATCH || rowData.stage === BOM_STAGES.COMPLETED) &&
                                              rowData.current_tracking_status === 'On Time') || rowData.stage === BOM_STAGES.VALIDATION;
                                          
                                              const label = isCompletedDone
                                                ? 'DONE'
                                                : getStatusLabel(rowData.current_tracking_status);
                                            
                                              const colors = isCompletedDone
                                                ? DELIVERY_CHIP_COLORS['On Time']
                                                : DELIVERY_CHIP_COLORS[rowData.current_tracking_status] || {
                                                  background: '#9e9e9e',
                                                  text: 'white',
                                                };
                                                return label === '-' ? null : 
                                                (
                                                    <Chip 
                                                        label={label}
                                                        size="small"
                                                        style={{ backgroundColor: colors.background, color: colors.text, fontWeight: 400, fontSize: '10px'}}
                                                    />
                                                );
                                            },
                                        },
                                      ],
                                      []
  );

  // Alert table columns (memoized)
  const alertTableColumns = useMemo(
    () => [
      { title: 'Fixture Number', field: 'documentNumber', cellStyle: { fontWeight: 'bold' } },
      { title: 'Fixture Name', field: 'fixtureName' },
      { title: 'Customer', field: 'customerName' },
      {
        title: 'Current Milestone',
        field: 'stage',
        render: (rowData) => {
          const chipStyle = { width: 140, fontWeight: 500, fontSize: '10px'};
          let backgroundColor;
          const stage = rowData.stage;
          switch (stage) {
            case BOM_STAGES.MANUFACTURING_EXECUTION:
              backgroundColor = STAGE_COLORS.MANUFACTURING_EXECUTION;
              break;
            case BOM_STAGES.TESTING:
              backgroundColor = STAGE_COLORS.TESTING;
              break;
            case BOM_STAGES.DISPATCH:
              backgroundColor = STAGE_COLORS.DISPATCH;
              break;
            case BOM_STAGES.DESIGN:
              backgroundColor = STAGE_COLORS.DESIGN;
              break;
            case BOM_STAGES.HANDOVER_TO_DISPATCH:
              backgroundColor = STAGE_COLORS.HANDOVER_TO_DISPATCH;
              break;
            case BOM_STAGES.MANUFACTURING_PLANNING:
              backgroundColor = STAGE_COLORS.MANUFACTURING_PLANNING;
              break;
            case BOM_STAGES.PROCUREMENT:
              backgroundColor = STAGE_COLORS.PROCUREMENT;
              break;
            case BOM_STAGES.COMPLETED:
              backgroundColor = STAGE_COLORS.COMPLETED;
              break;
            case BOM_STAGES.COMMERCIAL_CLEARANCE:
              backgroundColor = STAGE_COLORS.COMMERCIAL_CLEARANCE;
              break;
            case BOM_STAGES.VALIDATION:
            backgroundColor = STAGE_COLORS.VALIDATION;
              break;
            default:
              backgroundColor = '#616161';
              break;
          }
          const displayStage = rowData.stage
            ? rowData.stage.charAt(0).toUpperCase() + rowData.stage.slice(1)
            : 'N/A';
          return (
            <Chip
              label={displayStage}
              size="small"
              style={{ ...chipStyle, backgroundColor, color: '#000000' }}
            />
          );
        },
      },
      { title: 'Delivery Date', field: 'manufacturingDelivery' },
    ],
    []
  );

  const Kpi = ({ label, value }) => (
    <Card elevation={2} className="kpi-card" style={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle1" color="text.secondary" gutterBottom style={{ fontSize: '15px',  textAlign: 'center' }}>
          {label}
        </Typography>
        <Typography variant="h5" fontWeight={700}>
          {value ?? '-'}
        </Typography>
      </CardContent>
    </Card>
  );

  const CountList = ({ title, data, onClickItem, activeId, optionsMap }) => (
    <Card elevation={2} sx={{ height: '100%' }}>
        <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom style={{ borderBottom: '1px solid #eee', paddingBottom: 2, fontSize: '15px' }}>
                {title}
            </Typography>
            <Box sx={{ mt: 1, maxHeight: 200, overflowY: 'auto' }}>
                {data && data.length > 0 ? (
                    data.map((item, idx) => {
                        let itemId = item.id || item._id;
                        if (!itemId && optionsMap) {
                            const found = optionsMap.find(o => o.name === item.name);
                            if (found) itemId = found.id;
                        }
                        const isSelected = activeId && activeId !== 'All' && itemId === activeId;

                        return (
                        <Box key={idx} 
                             onClick={() => onClickItem && onClickItem(item)}
                             sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                mb: 1, 
                                cursor: onClickItem ? 'pointer' : 'default',
                                backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                                borderRadius: 1,
                                transition: 'background-color 0.2s',
                                '&:hover': {
                                    backgroundColor: isSelected ? '#bbdefb' : '#f5f5f5'
                                }
                             }}>
                            <Typography variant="body" color="text.secondary" sx={{ fontSize: '13px', fontWeight: isSelected ? 'bold' : 'normal' }}>{item.name}</Typography>
                            <Typography variant="body" fontWeight={700} sx={{ fontSize: '13px', pr:2 }}>{item.count}</Typography>
                        </Box>
                    )})
                ) : (
                    <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic', pl: 1 }}>No active orders</Typography>
                )}
            </Box>
        </CardContent>
    </Card>
  );

  //custom toolbar component
  const CustomToolbar = (props) => (
    <div>
      <MTableToolbar {...props} />
   
      <Box sx={{ 
        padding: { xs: '15px 8px 12px', sm: '0 16px 16px', md: '0 24px 16px' },
        mt: { xs: -2, sm: -3, md: 0 },
        overflowX: 'auto',
        width: '100%',
        display: 'flex',
        }}
      >
        <ToggleButtonGroup
          value={selectedStage}
          exclusive
          onChange={handleStageChange}
          size="small"
          aria-label="Filter by stage"
        >
                    {filterStages.map((stage) => {
                        const stageColor = getStageColor(stage); // Get the color
                        
                        return (
                            <ToggleButton
                                key={stage}
                                value={stage}
                                sx={{
                                    textTransform: 'capitalize',
                                    px: 1.5,
                                    py: 0.5,
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    flexShrink: 0,
                                    
                                    // --- Applied Colors ---
                                    color: 'black', // Text color
                                    backgroundColor: stageColor,
                                    border: '1px solid rgba(0,0,0,0.1)',

                                    // Style for the SELECTED button
                                    '&.Mui-selected': {
                                        backgroundColor: stageColor, // Keep the color
                                        color: 'black',
                                        fontWeight: 700,
                                        boxShadow: 'inset 0px 2px 4px rgba(0,0,0,0.2)', // "Pressed" look
                                        opacity: 2,
                                    },
                                    '&.Mui-selected:hover': {
                                        backgroundColor: stageColor,
                                        opacity: 0.9,
                                    },
                                    // Style for NON-selected button on hover
                                    '&:not(.Mui-selected):hover': {
                                        backgroundColor: stageColor,
                                        opacity: 0.75,
                                    },
                                    // Style for NON-selected button (base)
                                    '&:not(.Mui-selected)': {
                                        opacity: 1, // Fade non-selected buttons
                                    }
                                }}
                            >
                                {stage}
                            </ToggleButton>
                        );
                    })}
        </ToggleButtonGroup>
      </Box>
    </div>
  );

  const handleVerticalListClick = (item) => {
    let id = item.id || item._id;
    if (!id && verticalOptions.length) {
       const found = verticalOptions.find(v => v.name === item.name);
       if (found) id = found.id;
    }

    if (id) {
        setSelectedVertical(prev => prev === id ? 'All' : id);
    }
  };

  const handleRegionListClick = (item) => {
    let id = item.id || item._id;
    if (!id && regionOptions.length) {
       const found = regionOptions.find(r => r.name === item.name);
       if (found) id = found.id;
    }

    if (id) {
        setSelectedRegion(prev => prev === id ? 'All' : id);
    }
  };
  const totalOngoingByVertical = useMemo(() => {
    return (metrics?.ongoingByVertical || []).reduce(
      (sum, item) => sum + (item.count || 0),
      0
    );
  }, [metrics?.ongoingByVertical]);

  const totalOngoingByRegions = useMemo(() => {
    return (metrics?.ongoingByRegion || []).reduce(
      (sum, item) => sum + (item.count || 0),
      0
    );
  }, [metrics?.ongoingByRegion]);

const supplierChartData = useMemo(() => {
    const data = metrics.softStageBySupplier || [];
    return {
        names: data.map(d => d.supplierName + "   "),
        totalCounts: data.map(d => d.totalCount),
        pendingCounts: data.map(d => d.pendingCount),
    };
}, [metrics.softStageBySupplier]);

const goToBack = () => {
        const newDate = moment(currentDate).subtract(1, 'month').toDate();
        handleNavigate(newDate);
    };

    const goToNext = () => {
        const newDate = moment(currentDate).add(1, 'month').toDate();
        handleNavigate(newDate);
    };

    const monthBomCount = useMemo(() => {
        return calendarEvents.filter(event =>
            moment(event.start).isSame(currentDate, 'month')
        ).length;
    }, [calendarEvents, currentDate])


  return (
    <ThemeProvider theme={mytheme}>
      {isLoading ? (
        <AdminDashboardSkeleton />
      ) : (
<div className="main-content" style={{ backgroundColor: '#f0f0f0' }}>
                    <div className="pagetitle row" style={{ color: 'black', marginTop: "-25px" }}>
                        <div className="col-md-10 sm_device">
                            <SpaceDashboardIcon aria-hidden="true" />
                            <span style={{ marginLeft: '7px' }}>Dashboard</span>
                        </div>
                        <div className="col-md-2 sm_device" style={{ textAlign: 'right' }}>
                            <i className={`${CALENDER_ICON}`}></i> {moment().format('DD MMM YYYY')}
                        </div>
                    </div>

          <Divider sx={{ mb: 2, mt: 2 }} />

          <Box sx={{ px: { xs: 2, md: 0 }, pb: 1, mb: 1.5}}>
            <Grid container spacing={2} alignItems="stretch">

                {/* ================= LEFT COLUMN ================= */}
                <Grid item xs={12} md={6}>

                  {/* Orders Dispatched Total Count */}
                  <Card elevation={2} sx={{ borderLeft: '4px solid #2d4243'}}>
                    <CardContent sx={{ px: 2, py: 1.5 }}>
                      <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '16px', mb: 1 }}>
                        Orders Dispatched Total Count –
                      </Typography>

                      <Grid container justifyContent="space-around">
                        {metrics.dispatchedFY && metrics.dispatchedFY.length > 0 ? (
                            metrics.dispatchedFY.map((item, idx) => (
                            <Grid item key={idx} sx={{ textAlign: 'center', mt: -1, mb:-2 }}>
                                <Box sx={{ fontSize: '14px',fontWeight: 700, color: 'text.secondary'}}>
                                {item.name}
                                </Box>
                                <Box sx={{ fontSize: '14px', fontWeight: 700, color: 'text.primary' }}>
                                {item.count}
                                </Box>
                            </Grid>
                            ))
                            ) : (
                            <Grid item>
                            <Box sx={{ fontSize: '14px', color: 'text.secondary' }}>
                                No orders dispatched this FY matching criteria.
                            </Box>
                            </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </Card>

                  {/* === BV & REGION SIDE BY SIDE === */}
                  <Grid container spacing={2} sx={{ mt: 0 }}>

                    {/* Business Vertical */}
                    <Grid item xs={12} md={6}>
                      <Card elevation={2} sx={{
                          height: '100%',
                          '& .MuiTypography-root:first-of-type': {
                            fontSize: '14px', 
                          },
                          '& .MuiTableCell-root, & .MuiListItemText-primary': {
                            fontSize: '14px'    // table/list data
                          }
                        }}>
                        <CountList
                        title= {
                          <Box display="flex" justifyContent="space-between">
                            <span>Ongoing orders – Business Vertical</span>
                            <span style={{ marginRight: 10 }}>{totalOngoingByVertical}</span>
                          </Box>
                        }
                          data={metrics.ongoingByVertical}
                          onClickItem={handleVerticalListClick}
                          activeId={selectedVertical}
                          optionsMap={verticalOptions}
                        />
                      </Card>
                    </Grid>

                    {/* Region */}
                    <Grid item xs={12} md={6}>
                      <Card elevation={2} sx={{
                          height: '100%',
                          '& .MuiTypography-root:first-of-type': {
                            fontSize: '14px',
                          },
                          '& .MuiTableCell-root, & .MuiListItemText-primary': {
                            fontSize: '14px'    // table/list data
                          }
                        }}>
                        <CountList
                         title=
                         {
                          <Box display="flex" justifyContent="space-between">
                            <span>Ongoing orders – Region</span>
                            <span style={{ marginRight: 10 }}>{totalOngoingByRegions}</span>
                          </Box>
                        }
                          data={metrics.ongoingByRegion}
                          onClickItem={handleRegionListClick}
                          activeId={selectedRegion}
                          optionsMap={regionOptions}
                        />
                      </Card>
                    </Grid>

                  </Grid>
                </Grid>

                {/* ================= RIGHT COLUMN ================= */}
                <Grid item xs={12} md={6}>
                  <Card elevation={2} sx={{ height: '100%' }}>
                    <CardContent sx={{ p: 2 }}>
                      <Typography variant="subtitle1" fontWeight={700} gutterBottom style={{ borderBottom: '1px solid #eee',  paddingBottom: 2, fontSize: '16px' }}>
                                            Scheduled delivery next two weeks
                        </Typography>

                        <TableContainer sx={{ maxHeight: 300 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{fontWeight:'bold', fontSize:14, py: 1}}>Customer</TableCell>
                                        <TableCell sx={{fontWeight:'bold', fontSize:14, py: 1}}>End Client</TableCell>
                                        <TableCell sx={{fontWeight:'bold', fontSize:14, py: 1}}>Fixture No</TableCell>
                                        <TableCell sx={{fontWeight:'bold', fontSize:14, py: 1}}>Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {deliveryNextTwoWeeks.length > 0 ? (
                                        deliveryNextTwoWeeks.map((row) => (
                                            <TableRow key={row.bomId} hover>
                                                <TableCell sx={{fontSize:14, py: 0.5}}>{row.customerName}</TableCell>
                                                 <TableCell sx={{fontSize:14, py: 0.5}}>{row.end_client}</TableCell>
                                                <TableCell sx={{fontSize:14, py: 0.5}}>{row.documentNumber}</TableCell>
                                                <TableCell sx={{fontSize:14, color: '#d32f2f', fontWeight:600, py: 0.5}}>
                                                    {formatDateString(row.manufacturingDelivery)}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center" sx={{ fontSize: 14, color: '#999', py: 2 }}>
                                                No deliveries scheduled for next 2 weeks.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>
            </Grid>
          </Box>

          {/* Alert Table */}
          {delayedBoms.length > 0 && (
          <Box sx={{ px: { xs: 2, md: 0 }, pb: 1, mb: 1.5 }}>
              <Grid item xs={12} md={12}>
                  <div className="bom-table-div mb-5">
                    <MaterialTable
                      title={
                        <Box sx={{ display: 'flex', alignItems: 'center', color: '#c62828' }}>
                          <Warning sx={{fontSize: 24}} />
                          <Typography variant="subtitle1" fontWeight={700} ml={1} fontSize={16}>
                            Attention Needed
                          </Typography>
                        </Box>
                      }
                      columns={alertTableColumns}
                      data={delayedBoms}
                      onRowClick={(event, rowData) => {
                        if (isSales) {
                          // Sales can open only VALIDATION stage
                          if (rowData.stage === BOM_STAGES.VALIDATION) {
                            history.push(`/create-bom-page/${rowData.bomId}`);
                          }
                        } else {
                          history.push(`/create-bom-page/${rowData.bomId}`);
                        }
                      }}
                      options={{
                        headerStyle: {
                          color: '#333333',
                          fontWeight: 'bolder',
                          fontSize: 14,
                          backgroundColor: '#ffcdd2',
                        },
                        rowStyle: () => ({
                          backgroundColor:
                          isDesignEngineer || isProcurementEngineer
                            ? '#ffffff'   
                            : (DELIVERY_ROW_BG['Delayed'] || '#ffebee'),
                          textAlign: 'left',
                          cursor: 'pointer'
                        }),
                        padding: 'dense',
                        pageSize: 10,
                        pageSizeOptions: [5, 10],
                        toolbar: true,
                        search: true,
                        maxBodyHeight: 180,
                        tableLayout: 'fixed'
                      }}
                    />
                  </div>
              </Grid>
          </Box>
          )}
          
          <Box sx={{ px: { xs: 2, md: 0 }, pb: 1, mb: 1.5 }}>
            <Grid container spacing={2}>

                            {/* LEFT: Calendar (Approx 66%) */}
                            <Grid item xs={12} md={8}>
                                <Card elevation={2} sx={{ height: '650px' }}>
                                    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                                         <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, color: '#2d3d42' }}>
                                            
                                            {/* Title */}
                                            <Typography variant="subtitle1" fontWeight={700} fontSize={16}>
                                                <i className={CALENDER_ICON} style={{ marginRight: 8 }}></i>
                                                Scheduled Delivery Calendar
                                                
                                            </Typography>

                                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                                                <IconButton onClick={goToBack} size="small" sx={{ border: '0px solid #e0e0e0' }}>
                                                    <ArrowBackIos fontSize="inherit" />
                                                </IconButton>
                                                
                                                <Box sx={{ '& *': { fontSize: '16px !important' } }}>
                                                    <span style={{ fontSize: '1.25rem', fontWeight: 700, color: '#2d3d42' }}>
                                                        {moment(currentDate).format('MMMM YYYY')} :
                                                    </span>
                                                    <span style={{ marginLeft: 8, fontSize: '16px', fontWeight: 600, color: '#2d3d42' }}>
                                                    {monthBomCount}&nbsp;BOM Deliveries
                                                </span>
                                                </Box>
                                                
                                                <IconButton onClick={goToNext} size="small" sx={{ border: '0px solid #e0e0e0' }}>
                                                    <ArrowForwardIos fontSize="inherit" />
                                                </IconButton>
                                                
                                                
                                            </Box>
                                        </Box>

                                        <div style={{ flex: 1 }}>
                                        <Box sx={{ height: '100%','& .rbc-toolbar-label': {fontSize: '14px',fontWeight: 600,},'& .rbc-btn-group button': {fontSize: '14px',},'& .rbc-header': {fontSize: '14px',}, '& .rbc-date-cell': {fontSize: '14px',},
                                          }}
                                        >
                                            <Calendar
                                                localizer={localizer}
                                                events={calendarEvents}
                                                startAccessor="start"
                                                endAccessor="end"
                                                style={{ height: '100%', fontFamily: FONT_FAMILY }}
                                                views={['month', 'week']} 
                                                defaultView="month"
                                                view={currentView}
                                                onView={setCurrentView}
                                                date={currentDate}
                                                onNavigate={handleNavigate}
                                                popup={true}
                                                onSelectEvent={handleSelectEvent}
                                                toolbar={false} 
                                                components={{
                                                    event: CustomEvent,
                                                }}
                                                eventPropGetter={(event) => {
                                                  const status =
                                                    event.resource?.stage === 'Completed' ||
                                                    event.resource?.current_stage === BOM_STAGES.COMPLETED
                                                        ? 'Completed'
                                                        : event.resource?.stage === 'Validation' ||
                                                          event.resource?.current_stage === BOM_STAGES.VALIDATION
                                                        ? 'Validation'
                                                        : event.resource?.current_tracking_status || 'N/A';

                                                    const colorStyle = DELIVERY_ROW_BG[status] ? DELIVERY_CHIP_COLORS[status] : { background: '#ebebebff', text: '#2d3d42' };
                                                    return {
                                                        style: {
                                                            backgroundColor: colorStyle?.background || '#ebebebff',
                                                            color: colorStyle?.text || 'white',
                                                            borderRadius: '4px',
                                                            border: 'none',
                                                            fontSize: '12px',
                                                            padding: '1px 1px'
                                                        }
                                                    };
                                                }}
                                                messages={{
                                                    noEventsInRange: "No deliveries scheduled for this range.",
                                                }}
                                            />
                                        </Box>
                                        </div>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* RIGHT: Agenda List (Approx 33%) */}
                            <Grid item xs={12} md={4}>
                                <Card elevation={2} sx={{ height: '650px' }}>
                                    <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 0 }}>
                                        <Box sx={{ p: 2, borderBottom: '1px solid #eee', backgroundColor: '#fafafa' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                                                <Typography variant="subtitle1" fontWeight={700} fontSize={16} color="#2d3d42">
                                                    <FormatListBulleted sx={{ verticalAlign: 'middle', mr: 1, fontSize: 16 }} />
                                                    Upcoming Deliveries
                                                </Typography>
                                                 {/* <FormControl size="small" sx={{ minWidth: 120 }}>
                                                    <Select
                                                        value={agendaMonthFilter}
                                                        onChange={(e) => setAgendaMonthFilter(e.target.value)}
                                                        displayEmpty
                                                        inputProps={{ 'aria-label': 'Month Filter' }}
                                                        sx={{ fontSize: '14px', height: 30 }}
                                                    >
                                                        <MenuItem value="All" sx={{ fontSize: '14px' }}>All Months</MenuItem>
                                                        {(() => {
                                                            const months = [...new Set(calendarEvents.map(e => moment(e.start).format('MMM YYYY')))];
                                                            return months.map(m => (
                                                                <MenuItem key={m} value={m} sx={{ fontSize: '14px' }}>{m}</MenuItem>
                                                            ));
                                                        })()}
                                                    </Select>
                                                </FormControl> */}
                                            </Box>
                                            {/* <Typography variant="caption" color="text.secondary">
                                                {calendarEvents.length} scheduled deliveries found
                                            </Typography> */}
                                        </Box>
                                        <Box sx={{ overflowY: 'auto', flex: 1, p: 2 }}>
                                            {calendarEvents
                                                .filter(event => {
                                                    if (agendaMonthFilter === 'All') return true;
                                                    return moment(event.start).format('MMM YYYY') === agendaMonthFilter;
                                                })
                                                .sort((a, b) => new Date(a.start) - new Date(b.start))
                                                .map((event, index) => {
                                                    const isPast = new Date(event.start) < new Date();
                                                    const rowData = event.resource;
                                                    const displayStatus =
                                                    rowData?.stage === 'Completed' ||
                                                    rowData?.current_stage === BOM_STAGES.COMPLETED
                                                        ? 'Completed'
                                                        : rowData?.stage === 'Validation' ||
                                                          rowData?.current_stage === BOM_STAGES.VALIDATION
                                                        ? 'Validation'
                                                        : rowData?.current_tracking_status || 'N/A';

                                                    const statusColor = DELIVERY_CHIP_COLORS[displayStatus] || {
                                                        background: '#eee',
                                                        text: 'black'
                                                    };


                                                    return (
                                                        <Paper
                                                            key={index}
                                                            elevation={0}
                                                            onClick={() => handleSelectEvent(event)}
                                                            sx={{
                                                                mb: 1.5,
                                                                p: 1.5,
                                                                border: '1px solid #eee',
                                                                borderRadius: 2,
                                                                cursor: 'pointer',
                                                                transition: 'all 0.2s',
                                                                backgroundColor: isPast ? '#fcfcfc' : 'white',
                                                                opacity: isPast ? 0.7 : 1,
                                                                '&:hover': {
                                                                    borderColor: '#2d3d42',
                                                                    backgroundColor: '#f5fbfd',
                                                                    transform: 'translateY(-2px)',
                                                                    boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
                                                                }
                                                            }}
                                                        >
                                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                                                <Chip
                                                                    label={moment(event.start).format('DD MMM YYYY')}
                                                                    size="small"
                                                                    sx={{ borderRadius: 1, height: 20, fontSize: '12px', fontWeight: 700 }}
                                                                />
                                                                <Chip
                                                                    label={displayStatus}
                                                                    size="small"
                                                                    sx={{
                                                                        borderRadius: 1,
                                                                        height: 20,
                                                                        fontSize: '12px',
                                                                        backgroundColor: statusColor.background,
                                                                        color: statusColor.text
                                                                    }}
                                                                />
                                                            </Box>

                                                            <Typography fontWeight={700} fontSize="14px !important" sx={{ lineHeight: 1.2, mb: 0.5 }}>
                                                                {rowData.customerName}
                                                            </Typography>
                                                            <Typography fontSize="12px !important" color="text.secondary">
                                                                {rowData.documentNumber}
                                                            </Typography>
                                                            <Typography fontSize="12px !important" sx={{ mt: 0.5}}>
                                                                Stage: {rowData.stage}
                                                            </Typography>
                                                        </Paper>
                                                    );
                                                })
                                            }

                                            {calendarEvents.length === 0 && (
                                                <Box sx={{ textAlign: 'center', mt: 5, color: '#999' }}>
                                                    <Typography variant="body2">No events found for current filters.</Typography>
                                                </Box>
                                            )}
                                        </Box>

                                    </CardContent>
                                </Card>
                            </Grid>

            </Grid>
          </Box>            

          {/* Main Table */}
          <Box sx={{ px: { xs: 2, md: 0 }, pb: 1, mb: 1.5 }}>
            <Grid item xs={12} md={12}>
            <div className="bom-table-div mb-5">
              <MaterialTable
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', color: '#f1f1f1' }}>
                      <DvrIcon sx={{fontSize: 24, color: '#2d3d42' }} />
                      <Typography variant="subtitle1" fontWeight={700} ml={1} fontSize={16}>
                        Project Monitoring 
                      </Typography>
                    </Box>
                  }
                columns={mainTableColumns}
                data={bomList}
                onRowClick={(event, rowData) => {
                    setSelectedEventData(rowData);
                    setEventDialogOpen(true);
                }}
                options={{
                  ...mainTableOptions,

                  actionsColumnIndex: -1,
                  cellStyle: {
                    fontSize: '14px',
                  },
                  rowStyle: (rowData) => ({
                    backgroundColor: getRowBackground(rowData),
                    fontSize: '14px',
                  }),
                }}
                components={{
                  Toolbar: (props) => <CustomToolbar {...props} />,
                }}
              />
              </div>
            </Grid>
          </Box>
          
          <Box sx={{ px: { xs: 2, md: 0 }, pb: 1, mb: 1.5 }}>
            <Grid item xs={12} md={9}>
                <Card elevation={2} sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', color: '#2d3d42' }}>
                      <StackedBarChart sx={{ fontSize: 24 }} />  
                      <Typography variant="subtitle1" fontWeight={700} fontSize={16}>
                        BOM Stages
                      </Typography>
                    </Box>
                    {metrics?.stageCounts?.length ? (
                      <Plot
                        data={[
                          {
                            type: 'bar',
                            x: metrics.stageCounts.map((s) => s.stage || '-'),
                            y: metrics.stageCounts.map((s) => s.count),
                            text: metrics.stageCounts.map((s) => s.count),
                            textposition: 'outside',
                            marker: { color: stagePlotColors },
                          },
                        ]}
                        layout={{
                          title: '',
                          height: 320,
                          autosize: true,
                          font: {
                            family: FONT_FAMILY,
                            size: 12,
                            color: '#333'
                          },
                          margin: { t: 20, b: 60, l: 50, r: 20 },
                          xaxis: { 
                              type: 'category', 
                              title: { text: 'Stage', standoff: 20 },
                              tickfont: { size: 12 },
                              fixedrange: true,
                              automargin: true
                          },
                          yaxis: { 
                              title: 'BOMs', 
                              tickformat: ',.0f',
                              range: [0, commonYAxisMax],
                              fixedrange: true,
                              gridcolor: '#eee',
                              automargin: true
                          },
                          barmode: 'group',
                          bargap: 0.6,
                          bargroupgap: 0.06,
                        }}
                        style={{ width: '100%' }}
                        useResizeHandler
                        config={{ displayModeBar: false }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        No data to display.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
          </Box>
          {!isSales && (
            <Box sx={{ px: { xs: 2, md: 0 }, pb: 1, mb: 1.5 }}>
              <Grid container spacing={2}>

                {/* Chart 1 */}
                <Grid item xs={12} md={3}>
                  <Card elevation={2} sx={{ height: '100%' }}>
                    <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', color: '#2d3d42' }}>
                          <BarChart sx={{ fontSize: 24 }} />
                          <Typography variant="subtitle1" fontWeight={700} fontSize={16}>
                          Manufacturing Execution Status
                          </Typography>
                        </Box>
                      {metrics?.statusCounts?.length ? (
                        <Plot
                          data={[
                            {
                              type: 'bar',
                              x: metrics.statusCounts.map((s) => s.status || '-'),
                              y: metrics.statusCounts.map((s) => s.count),
                              text: metrics.statusCounts.map((s) => s.count),
                              textposition: 'outside',
                              marker: { color: statusColors },
                            },
                          ]}
                          layout={{
                            title: '',
                            height: 320,
                            autosize: true,
                            font: {
                              family: FONT_FAMILY,
                              size: 12,
                              color: '#333'
                            },
                            margin: { t: 20, b: 60, l: 50, r: 20 },
                            xaxis: { 
                                type: 'category', 
                                title: { text: 'Status', standoff: 20 },
                                tickfont: { size: 12 },
                                fixedrange: true,
                                automargin: true
                            },
                            yaxis: { 
                                title: 'BOMs', 
                                tickformat: ',.0f', 
                                range: [0, commonYAxisMax],
                                fixedrange: true,
                                gridcolor: '#eee',
                                automargin: true
                            },
                            barmode: 'group',
                            bargap: 0.6, 
                          }}
                          style={{ width: '100%' }}
                          useResizeHandler
                          config={{ displayModeBar: false }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No data to display.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Chart 2 */}
            <Grid item xs={12} md={9}>
                <Card elevation={2}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', color: '#2d3d42' }}>
                      <BarChart sx={{ fontSize: 24 }} />
                      <Typography variant="subtitle1" fontWeight={700} fontSize={16}>
                        Supplier vs BOM (Soft Stage Pending)
                      </Typography>
                    </Box>
                    

                    {supplierChartData.names.length > 0 ? (
                    <Plot
                      data={[
                        // Trace 1: Soft Stage Pending (Blue)
                        {
                          type: 'bar',
                          name: 'Soft Stage Pending',
                          y: supplierChartData.names,
                          x: supplierChartData.pendingCounts,
                          orientation: 'h',
                          text: supplierChartData.pendingCounts.map(String),
                          textposition: 'outside',
                          marker: {
                            color: '#ED7D31',
                            opacity: 0.9,
                          },
                          hovertemplate: '<b>%{y}</b><br>Soft Stage Pending: %{x}<extra></extra>',
                        },
                        // Trace 2: Total Allocation
                        {
                          type: 'bar',
                          name: 'Total Allocation',
                          y: supplierChartData.names, 
                          x: supplierChartData.totalCounts, 
                          orientation: 'h',
                          text: supplierChartData.totalCounts.map(String),
                          textposition: 'outside',
                          marker: {
                            color: '#3366CC',
                            opacity: 0.9,
                          },
                          hovertemplate: '<b>%{y}</b><br>Total Allocation: %{x}<extra></extra>',
                        }
                      ]}
                      layout={{
                        title: '',
                        height: 320,
                        autosize: true,
                        barmode: 'group', 
                        bargap: 0.5,      
                        bargroupgap: 0.1, 
                        font: {
                          family: FONT_FAMILY,
                          size: 12,
                          color: '#333'
                        },
                        margin: { t: 20, b: 40, l: 160, r: 20 }, 
                        
                        xaxis: {
                          title: { text: 'Count', standoff: 20 },
                          showgrid: true,
                          gridcolor: '#eee',
                          zeroline: false,
                          fixedrange: true,
                          automargin: true
                        },
                        yaxis: {
                          type: 'category',
                          fixedrange: true,
                          autorange: 'reversed', 
                          showgrid: false,
                          automargin: true
                        },
                        // legend: {
                        //   orientation: 'h',
                        //   y: -0.2,
                        //   x: 0.3
                        // },
                        paper_bgcolor: 'rgba(0,0,0,0)',
                        plot_bgcolor: 'rgba(0,0,0,0)',
                      }}
                      style={{ width: '100%' }}
                      useResizeHandler
                      config={{ displayModeBar: false, responsive: true }}
                    />
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                        No supplier data available.
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              </Grid>
            </Box>
          )}

          {/* <Box sx={{ px: { xs: 2, md: 0 }, pb: 1, mb: 1.5 }}>
            <Grid container spacing={2}>

                <Grid item xs={12} md={6}>

                  <Card elevation={2} sx={{ height: 'auto' }}>
                    <CardContent sx={{ p: 2 }}>
                        <Typography variant="subtitle1" fontWeight={700} gutterBottom style={{ borderBottom: '1px solid #eee', paddingBottom: 2, fontSize: '16px' }}>
                            Partial Procurement Pending
                        </Typography>

                        <TableContainer sx={{ maxHeight: 200 }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{fontWeight:'bold', fontSize:14, py: 1}}>Fixture No</TableCell>
                                        <TableCell sx={{fontWeight:'bold', fontSize:14, py: 1}}>Items Pending</TableCell>
                                        <TableCell sx={{fontWeight:'bold', fontSize:14, py: 1}}>Delivery Date</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {partialProcurementList.length > 0 ? (
                                        partialProcurementList.map((row) => {
                                            // Calculate counts
                                            const totalItems = row.items ? row.items.length : 0;
                                            const pendingItems = row.items ? row.items.filter(i => !i.procurement_done).length : 0;
                                            
                                            return (
                                                <TableRow key={row.bomId} hover>
                                                    <TableCell sx={{fontSize:14, py: 0.5}}>
                                                        {row.documentNumber}
                                                        <div style={{fontSize: 10, color: '#666'}}>{row.customerName}</div>
                                                    </TableCell>
                                                    <TableCell sx={{fontSize:14, py: 0.5}}>
                                                        <span style={{color: '#d32f2f', fontWeight: 'bold'}}>{pendingItems}</span> / {totalItems}
                                                    </TableCell>
                                                    <TableCell sx={{fontSize:14, py: 0.5, fontWeight: 600}}>
                                                        {row.manufacturingDelivery || '-'}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center" sx={{ fontSize: 14, color: '#999', py: 2 }}>
                                                No partial procurements pending.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </CardContent>
                  </Card>

                </Grid>
            </Grid>
          </Box> */}
        </div>
      )}
      {/* Event Details Dialog*/}
      {eventDialogOpen && selectedEventData && (
          <div className="custom_modal" onClick={() => setEventDialogOpen(false)}>
              <div className="modal_body" onClick={(e) => e.stopPropagation()}>
                  <button
                      type="button"
                      className="close text-right"
                      style={{fontSize: '2.5rem'}}
                      onClick={() => setEventDialogOpen(false)}
                  >
                      &times;
                  </button>
                  <h4>BOM Details</h4>
                  <hr />
                  <Box 
                      sx={{ 
                          '& .MuiStepLabel-label': { 
                              fontWeight: '400 !important',
                              fontSize: '12px !important'              
                          },
                          '& .MuiStepLabel-label.Mui-active': { 
                              fontWeight: '600 !important'  
                          }
                      }}
                  >
                      <BomProgressBar 
                        currentStage={selectedEventData.stage} 
                        stageTimelines={selectedEventData.stage_timelines} 
                      />  
                  </Box>                             
                  { selectedEventData.stage === BOM_STAGES.MANUFACTURING_EXECUTION && ( 
                  <>
                  {(() => {
                      const status = selectedEventData.current_tracking_status || '-';
                      let statusClass = 'status-default';
                      if (status === DELIVERY_STATUS.ON_TIME) statusClass = 'status-on-time';
                      else if (status === DELIVERY_STATUS.WARNING) statusClass = 'status-warning';
                      else if (status === DELIVERY_STATUS.DELAYED) statusClass = 'status-delayed';
                      else if (status === DELIVERY_STATUS.PENDING) statusClass = 'status-pending';
                      else if (status === DELIVERY_STATUS.PAUSED) statusClass = 'status-paused';

                      return (
                          <>
                              <div className="detail_row">
                                  <div className="detail_label_progress">Fixture Progress:</div>
                                  <div className="detail_value_progress">
                                      <div className="progress-bar-container">
                                          <div 
                                              className={`progress-bar ${statusClass}`} 
                                              style={{ width: `${selectedEventData.completion_percentage || 0}%` }}
                                          >
                                              {selectedEventData.completion_percentage || 0}%
                                          </div>
                                      </div>
                                  </div>
                              </div>
                              <div className="detail_row">
                                  <div className="detail_label_progress">Tracking Status:</div>
                                  <div className="detail_value_progress">
                                      <span className={`status-badge ${statusClass}`}>{status}</span>
                                  </div>
                              </div>
                              
                          </>
                      );
                  })()}
                  </>
                  )}
                  <div className="modal_content">
                      {fieldsToShow.map(({ key, label }) => {
                          const value = selectedEventData[key];
                          let displayValue = Array.isArray(value) 
                              ? (value.length > 0 ? value.join(', ') : '-') 
                              : (value || '-');

                          return (
                          <div key={key} className="detail_row">
                              <div className="detail_label">{label}:</div>
                              <div className="detail_value">{displayValue}</div>
                          </div>
                          );
                      })}
                  </div>


                  {(selectedEventData.stage === BOM_STAGES.VALIDATION || selectedEventData.stage === BOM_STAGES.COMPLETED || selectedEventData.stage === BOM_STAGES.HANDOVER_TO_DISPATCH) && (
                      <>
                          <div className="detail_label">Dispatch Details</div>
                          <hr />
                          <div className="modal_content">
                              {dispatch_details.map(({ key, label }) => {
                                  let value = selectedEventData?.dispatch_details ? selectedEventData.dispatch_details[key] : undefined;
                                  
                                  if (key === 'dispatch_date' && value) {
                                      const dateVal = value.$date ? value.$date : value;
                                      value = new Date(dateVal).toLocaleDateString();
                                      if (value === 'Invalid Date') value = '-';
                                  }

                                  let displayValue = Array.isArray(value) 
                                      ? (value.length > 0 ? value.join(', ') : '-') 
                                      : (value || '-');

                                  return (
                                  <div key={key} className="detail_row">
                                      <div className="detail_label">{label}:</div>
                                      <div className="detail_value">{displayValue}</div>
                                  </div>
                                  );
                              })}
                          </div>
                      </>
                  )}
              </div>
          </div>
      )}
    </ThemeProvider>
  );
};

export default AdminDashboardPage;
